import React, { useState, useEffect } from 'react';
import AdminLayout from '../../components/AdminLayout';
import { useAuth } from '../../components/AuthContext';
import { useToast } from '../../components/Toast';
import { api } from '../../lib/api';

const PLAN_CONFIG = {
  pro:   { label: 'Pro Plan',   color: '#d97706', bg: '#fffbeb', border: '#f59e0b', icon: '⭐' },
  basic: { label: 'Basic Plan', color: '#1a5c2a', bg: '#f0fdf4', border: '#4ade80', icon: '✅' },
  free:  { label: 'Free Plan',  color: '#6b7280', bg: '#f3f4f6', border: '#e5e7eb', icon: '🆓' },
};

const styles = `
  .prof-root { max-width: 680px; }

  /* ── Hero card ── */
  .prof-hero {
    background: linear-gradient(135deg, #0a1f0f 0%, #1a5c2a 100%);
    border-radius: 20px;
    padding: 32px;
    margin-bottom: 20px;
    position: relative;
    overflow: hidden;
  }
  .prof-hero::after {
    content: '';
    position: absolute; inset: 0;
    background-image: radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px);
    background-size: 24px 24px;
    pointer-events: none;
  }
  .prof-hero-top {
    display: flex; align-items: center; gap: 20px;
    position: relative; z-index: 1;
  }
  .prof-avatar {
    width: 80px; height: 80px; border-radius: 50%;
    background: rgba(255,255,255,0.15);
    border: 2px solid rgba(255,255,255,0.25);
    display: flex; align-items: center; justify-content: center;
    font-size: 32px; font-weight: 800; color: white;
    flex-shrink: 0; letter-spacing: -1px;
    backdrop-filter: blur(8px);
  }
  .prof-hero-name {
    font-family: 'Syne', sans-serif;
    font-size: 24px; font-weight: 800; color: white;
    letter-spacing: -0.5px; margin-bottom: 4px;
  }
  .prof-hero-email { font-size: 14px; color: rgba(255,255,255,0.6); margin-bottom: 10px; }
  .prof-plan-badge {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 4px 12px; border-radius: 50px;
    font-size: 12px; font-weight: 700;
    border: 1px solid rgba(255,255,255,0.2);
    background: rgba(255,255,255,0.1);
    color: white; backdrop-filter: blur(4px);
  }
  .prof-hero-stats {
    display: grid; grid-template-columns: repeat(3, 1fr);
    gap: 1px; margin-top: 24px;
    background: rgba(255,255,255,0.1);
    border-radius: 14px; overflow: hidden;
    position: relative; z-index: 1;
  }
  .prof-stat {
    background: rgba(255,255,255,0.05);
    padding: 16px; text-align: center;
    backdrop-filter: blur(4px);
  }
  .prof-stat-val {
    font-family: 'Syne', sans-serif;
    font-size: 26px; font-weight: 800; color: #4ade80;
    line-height: 1;
  }
  .prof-stat-label { font-size: 11px; color: rgba(255,255,255,0.5); margin-top: 4px; letter-spacing: 0.5px; }

  /* ── Cards ── */
  .prof-card {
    background: white;
    border: 1px solid #e8f0e8;
    border-radius: 16px;
    overflow: hidden;
    margin-bottom: 16px;
  }
  .prof-card-header {
    padding: 18px 24px;
    border-bottom: 1px solid #f0f0f0;
    display: flex; align-items: center; justify-content: space-between;
  }
  .prof-card-title {
    font-family: 'Syne', sans-serif;
    font-size: 15px; font-weight: 700; color: #0f1117;
    display: flex; align-items: center; gap: 8px;
  }
  .prof-card-body { padding: 24px; }

  /* ── Form elements ── */
  .prof-field { margin-bottom: 22px; }
  .prof-field:last-child { margin-bottom: 0; }
  .prof-label {
    display: block; font-size: 12px; font-weight: 700;
    color: #6b7280; letter-spacing: 0.8px;
    text-transform: uppercase; margin-bottom: 6px;
  }
    .prof-input {
    width: 100%; padding: 13px 16px;
    border: 1.5px solid #e5e7eb; border-radius: 10px;
    font-size: 15px; color: #0f1117;
    font-family: 'DM Sans', sans-serif;
    transition: all 0.15s; outline: none;
    background: #fafafa;
    box-sizing: border-box;
  }
  .prof-input:focus {
    border-color: #1a5c2a;
    background: white;
    box-shadow: 0 0 0 3px rgba(26,92,42,0.08);
  }
  .prof-input-wrap { position: relative; }
  .prof-input-wrap .prof-input { padding-right: 44px; }
  .prof-eye {
    position: absolute; right: 12px; top: 50%;
    transform: translateY(-50%);
    background: none; border: none; cursor: pointer;
    font-size: 16px; padding: 4px; line-height: 1;
  }
  .prof-pw-strength {
    display: flex; gap: 4px; margin-top: 8px;
  }
  .prof-pw-bar {
    height: 3px; flex: 1; border-radius: 2px;
    background: #e5e7eb; transition: background 0.2s;
  }
  .prof-pw-hint { font-size: 11px; color: #9ca3af; margin-top: 4px; }
  .prof-match-error { font-size: 12px; color: #ef4444; margin-top: 4px; display: flex; align-items: center; gap: 4px; }
  .prof-match-ok { font-size: 12px; color: #16a34a; margin-top: 4px; display: flex; align-items: center; gap: 4px; }

  /* ── Buttons ── */
  .prof-btn {
    padding: 11px 24px; border-radius: 10px;
    font-family: 'DM Sans', sans-serif;
    font-size: 14px; font-weight: 700;
    cursor: pointer; border: none;
    transition: all 0.15s; display: inline-flex;
    align-items: center; gap: 8px;
  }
  .prof-btn-primary {
    background: #1a5c2a; color: white;
    box-shadow: 0 2px 8px rgba(26,92,42,0.25);
  }
  .prof-btn-primary:hover:not(:disabled) {
    background: #145022;
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(26,92,42,0.3);
  }
  .prof-btn-primary:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }
  .prof-btn-outline {
    background: white; color: #374151;
    border: 1.5px solid #e5e7eb;
  }
  .prof-btn-outline:hover { border-color: #1a5c2a; color: #1a5c2a; }

  /* ── Restaurant link ── */
  .prof-rest-link {
    display: flex; align-items: center; justify-content: space-between;
    padding: 14px 16px;
    background: #f8faf8; border: 1px solid #e8f0e8;
    border-radius: 12px; text-decoration: none;
    transition: all 0.15s;
  }
  .prof-rest-link:hover {
    background: #f0fdf4; border-color: #1a5c2a;
    transform: translateX(2px);
  }
  .prof-rest-name { font-size: 14px; font-weight: 700; color: #0f1117; }
  .prof-rest-url { font-size: 12px; color: #9ca3af; margin-top: 2px; }
  .prof-rest-arrow { font-size: 18px; color: #1a5c2a; }

  /* ── Info rows ── */
  .prof-info-row {
    display: flex; justify-content: space-between; align-items: center;
    padding: 12px 0; border-bottom: 1px solid #f5f5f5;
  }
  .prof-info-row:last-child { border-bottom: none; padding-bottom: 0; }
  .prof-info-label { font-size: 13px; color: #9ca3af; }
  .prof-info-val { font-size: 13px; font-weight: 600; color: #374151; }

  /* ── Danger zone ── */
  .prof-danger {
    background: #fff5f5; border: 1px solid #fecaca;
    border-radius: 16px; padding: 20px 24px;
    margin-bottom: 16px;
  }
  .prof-danger-title { font-size: 13px; font-weight: 700; color: #dc2626; margin-bottom: 4px; }
  .prof-danger-desc { font-size: 12px; color: #9ca3af; margin-bottom: 12px; }

  /* ── Support footer ── */
  .prof-support {
    text-align: center; padding: 16px;
    font-size: 13px; color: #9ca3af;
  }
  .prof-support a { color: #1a5c2a; font-weight: 600; text-decoration: none; }
  .prof-support a:hover { text-decoration: underline; }

  /* ── Mobile ── */
  @media (max-width: 600px) {
    .prof-hero { padding: 24px 20px; }
    .prof-hero-stats { grid-template-columns: repeat(3, 1fr); }
    .prof-stat-val { font-size: 20px; }
    .prof-card-body { padding: 18px; }
  }
`;

function getPasswordStrength(pw) {
  if (!pw) return 0;
  let score = 0;
  if (pw.length >= 6) score++;
  if (pw.length >= 10) score++;
  if (/[A-Z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  return score;
}

function StrengthBar({ password }) {
  const strength = getPasswordStrength(password);
  const colors = ['#e5e7eb', '#ef4444', '#f59e0b', '#f59e0b', '#22c55e', '#16a34a'];
  const labels = ['', 'Too weak', 'Weak', 'Fair', 'Good', 'Strong'];
  if (!password) return null;
  return (
    <div>
      <div className="prof-pw-strength">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="prof-pw-bar" style={{ background: i <= strength ? colors[strength] : '#e5e7eb' }} />
        ))}
      </div>
      <div className="prof-pw-hint" style={{ color: colors[strength] }}>{labels[strength]}</div>
    </div>
  );
}

export default function Profile() {
  const { user, setUser } = useAuth();
  const toast = useToast();

  const [profileData, setProfileData] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  const [profileForm, setProfileForm] = useState({ name: '', email: '', phone: '' });
  const [passwordForm, setPasswordForm] = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showPasswordForm, setShowPasswordForm] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/auth/me'),
      api.get('/restaurants'),
    ]).then(([userData, restData]) => {
      setProfileData(userData);
      setProfileForm({
        name: userData.name || '',
        email: userData.email || '',
        phone: userData.phone || '',
      });
      if (restData.length > 0) {
        setRestaurant(restData[0]);
        api.get(`/restaurants/${restData[0].restaurant_id}/stats`)
          .then(setStats).catch(() => {});
      }
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const handleProfileSave = async (e) => {
    e.preventDefault();
    if (!profileForm.name.trim()) { toast.error('Name is required'); return; }
    setSavingProfile(true);
    try {
      const updated = await api.put('/auth/profile', profileForm);
      setUser(updated);
      setProfileData(updated);
      toast.success('Profile updated!');
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSave = async (e) => {
    e.preventDefault();
    if (!passwordForm.current_password) { toast.error('Current password is required'); return; }
    if (passwordForm.new_password.length < 6) { toast.error('New password must be at least 6 characters'); return; }
    if (passwordForm.new_password !== passwordForm.confirm_password) { toast.error('Passwords do not match'); return; }
    setSavingPassword(true);
    try {
      await api.put('/auth/change-password', {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      toast.success('Password changed!');
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPassword(false);
    }
  };

  const plan = profileData?.plan || 'free';
  const planCfg = PLAN_CONFIG[plan] || PLAN_CONFIG.free;
  const memberSince = profileData?.created_at
    ? new Date(profileData.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : '—';

  if (loading) return (
    <AdminLayout title="My Profile">
      <div className="loading-center"><div className="loading-spinner" /></div>
    </AdminLayout>
  );

  return (
    <AdminLayout title="My Profile">
      <style>{styles}</style>
      <div className="prof-root">

        {/* ── Hero ── */}
        <div className="prof-hero">
          <div className="prof-hero-top">
            <div className="prof-avatar">
              {profileData?.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div>
              <div className="prof-hero-name">{profileData?.name}</div>
              <div className="prof-hero-email">{profileData?.email}</div>
              <div className="prof-plan-badge">
                {planCfg.icon} {planCfg.label}
              </div>
            </div>
          </div>

          {/* Stats */}
          <div className="prof-hero-stats">
            <div className="prof-stat">
              <div className="prof-stat-val">{stats?.total_items ?? '—'}</div>
              <div className="prof-stat-label">Menu Items</div>
            </div>
            <div className="prof-stat">
              <div className="prof-stat-val">{stats?.qr_scans ?? '—'}</div>
              <div className="prof-stat-label">QR Scans</div>
            </div>
            <div className="prof-stat">
              <div className="prof-stat-val">{stats?.total_categories ?? '—'}</div>
              <div className="prof-stat-label">Categories</div>
            </div>
          </div>
        </div>

        {/* ── Restaurant link ── */}
        {restaurant && (
          <div className="prof-card">
            <div className="prof-card-header">
              <div className="prof-card-title">🍽️ Your Restaurant</div>
            </div>
            <div className="prof-card-body" style={{ paddingTop: 16, paddingBottom: 16 }}>
              <a
                              
                href={restaurant.slug ? `/menu/${restaurant.slug}` : '#'}
                target="_blank"
                rel="noreferrer"
                className="prof-rest-link"
              >
                <div>
                  <div className="prof-rest-name">{restaurant.name}</div>
                  <div className="prof-rest-url">getqrdigimenu.com/menu/{restaurant.slug}</div>
                </div>
                <div className="prof-rest-arrow">↗</div>
              </a>
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`https://getqrdigimenu.com/menu/${restaurant.slug}`);
                  toast.success('Menu URL copied!');
                }}
                style={{ width: '100%', marginTop: 10, padding: '10px', background: '#f0fdf4', border: '1.5px solid #4ade80', borderRadius: 10, fontSize: 13, fontWeight: 700, color: '#1a5c2a', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
              >
                📋 Copy Menu URL
              </button>
            </div>
          </div>
        )}

        {/* ── Account info ── */}
        <div className="prof-card">
          <div className="prof-card-header">
            <div className="prof-card-title">📋 Account Details</div>
          </div>
          <div className="prof-card-body">
            <div className="prof-info-row">
              <span className="prof-info-label">Member since</span>
              <span className="prof-info-val">{memberSince}</span>
            </div>
            <div className="prof-info-row">
              <span className="prof-info-label">Account role</span>
              <span className="prof-info-val" style={{ textTransform: 'capitalize' }}>{profileData?.role || 'Owner'}</span>
            </div>
            <div className="prof-info-row">
              <span className="prof-info-label">Current plan</span>
              <span className="prof-info-val" style={{ color: planCfg.color }}>{planCfg.icon} {planCfg.label}</span>
            </div>
            <div className="prof-info-row">
              <span className="prof-info-label">Plan status</span>
              <span className="prof-info-val" style={{ color: profileData?.plan_status === 'active' ? '#16a34a' : '#6b7280', textTransform: 'capitalize' }}>
                {profileData?.plan_status || 'Free tier'}
              </span>
            </div>
          </div>
        </div>

        {/* ── Personal info ── */}
        <div className="prof-card">
          <div className="prof-card-header">
            <div className="prof-card-title">👤 Personal Information</div>
          </div>
          <div className="prof-card-body">
            <form onSubmit={handleProfileSave}>
              <div className="prof-field">
                <label className="prof-label">Full Name</label>
                <input
                  className="prof-input"
                  value={profileForm.name}
                  onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))}
                  placeholder="Your full name"
                  required
                />
              </div>
              <div className="prof-field">
                <label className="prof-label">Email Address</label>
                <input
                  type="email"
                  className="prof-input"
                  value={profileForm.email}
                  onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))}
                  placeholder="you@example.com"
                  required
                />
              </div>
              <div className="prof-field">
                <label className="prof-label">Phone Number</label>
                <input
                  className="prof-input"
                  value={profileForm.phone}
                  onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))}
                  placeholder="+1 (555) 000-0000"
                />
              </div>
              <button type="submit" className="prof-btn prof-btn-primary" disabled={savingProfile}>
                {savingProfile ? '⏳ Saving...' : 'Save changes'}
              </button>
            </form>
          </div>
        </div>

        {/* ── Change password ── */}
        <div className="prof-card">
          <div className="prof-card-header">
            <div className="prof-card-title">🔒 Change Password</div>
            <button
              type="button"
              className="prof-btn prof-btn-outline"
              style={{ fontSize: 13, padding: '7px 16px' }}
              onClick={() => setShowPasswordForm(p => !p)}
            >
              {showPasswordForm ? 'Cancel' : 'Change Password'}
            </button>
          </div>
          {showPasswordForm && (
          <div className="prof-card-body">
            <form onSubmit={handlePasswordSave}>
              <div className="prof-field">
                <label className="prof-label">Current Password</label>
                <div className="prof-input-wrap">
                  <input
                    type={showCurrent ? 'text' : 'password'}
                    className="prof-input"
                    value={passwordForm.current_password}
                    onChange={e => setPasswordForm(p => ({ ...p, current_password: e.target.value }))}
                    placeholder="Enter current password"
                    required
                  />
                  <button type="button" className="prof-eye" onClick={() => setShowCurrent(!showCurrent)}>
                    {showCurrent ? '🙈' : '👁️'}
                  </button>
                </div>
              </div>
              <div className="prof-field">
                <label className="prof-label">New Password</label>
                <div className="prof-input-wrap">
                  <input
                    type={showNew ? 'text' : 'password'}
                    className="prof-input"
                    value={passwordForm.new_password}
                    onChange={e => setPasswordForm(p => ({ ...p, new_password: e.target.value }))}
                    placeholder="Min. 6 characters"
                    required
                  />
                  <button type="button" className="prof-eye" onClick={() => setShowNew(!showNew)}>
                    {showNew ? '🙈' : '👁️'}
                  </button>
                </div>
                <StrengthBar password={passwordForm.new_password} />
              </div>
              <div className="prof-field">
                <label className="prof-label">Confirm New Password</label>
                <div className="prof-input-wrap">
                  <input
                    type={showConfirm ? 'text' : 'password'}
                    className="prof-input"
                    value={passwordForm.confirm_password}
                    onChange={e => setPasswordForm(p => ({ ...p, confirm_password: e.target.value }))}
                    placeholder="Re-enter new password"
                    required
                  />
                  <button type="button" className="prof-eye" onClick={() => setShowConfirm(!showConfirm)}>
                    {showConfirm ? '🙈' : '👁️'}
                  </button>
                </div>
                {passwordForm.confirm_password && (
                  passwordForm.new_password !== passwordForm.confirm_password
                    ? <div className="prof-match-error">✕ Passwords don't match</div>
                    : <div className="prof-match-ok">✓ Passwords match</div>
                )}
              </div>
              <button type="submit" className="prof-btn prof-btn-primary" disabled={savingPassword}>
                {savingPassword ? '⏳ Updating...' : 'Update password'}
              </button>
            </form>
          </div>
          )}
        </div>

        {/* ── Danger zone ── */}
                <div className="prof-danger">
          <div className="prof-danger-title">⚠️ Sign out everywhere</div>
          <div className="prof-danger-desc">
            Signs you out of all active sessions. You'll need to log in again.
          </div>
          <button
            className="prof-btn prof-btn-outline"
            style={{ fontSize: 13, padding: '8px 16px', color: '#dc2626', borderColor: '#fecaca' }}
            onClick={() => {
              if (window.confirm('Sign out of all sessions?')) {
                sessionStorage.clear();
                window.location.href = '/login';
              }
            }}
          >
            Sign out all sessions
          </button>

          <div style={{ borderTop: '1px solid #fecaca', marginTop: 20, paddingTop: 20 }}>
            <div className="prof-danger-title">🗑️ Delete Account</div>
            <div className="prof-danger-desc">
              Permanently deletes your account, restaurant, menu items, and all data. This cannot be undone.
            </div>
            <button
              className="prof-btn prof-btn-outline"
              style={{ fontSize: 13, padding: '8px 16px', color: '#dc2626', borderColor: '#fecaca', background: '#fff5f5' }}
              onClick={async () => {
                const confirm1 = window.confirm('Are you sure you want to delete your account? This cannot be undone.');
                if (!confirm1) return;
                const confirm2 = window.confirm('This will permanently delete all your menu items, categories, and restaurant data. Type OK to confirm.');
                if (!confirm2) return;
                try {
                  await api.delete('/auth/account');
                  sessionStorage.clear();
                  window.location.href = '/';
                } catch (err) {
                  toast.error(err.message || 'Failed to delete account.');
                }
              }}
            >
              🗑️ Delete My Account
            </button>
          </div>
        </div>

        {/* ── Support ── */}
        <div className="prof-support">
          Need help? <a href="mailto:hello@getqrdigimenu.com">hello@getqrdigimenu.com</a>
        </div>

      </div>
    </AdminLayout>
  );
}